import { useState } from 'react'
import { Link } from 'react-router-dom'

import Disclosure from './Disclosure.jsx'
import { releases } from '../content.js'

// The announcement bar. P6-83, MP2.
//
// 🔴 THE PORTAL IS THE ANNOUNCEMENT'S LANDING SURFACE — spec §4.1 lists
// *"what's new (the latest release-note entries, which makes the portal the
// announcement's landing surface)"* as one of the four modules. This is that,
// collapsed to one row until somebody asks for more.
//
// ⚠️ NOTHING PUBLISHED → NOTHING RENDERED, and that is not the same as a slot.
// `releases.entries` is the PUBLISHED half of content-src/releases/*.md; a
// withheld draft never enters the document at all (see src/routes/Releases.jsx,
// which explains why there is no status filter anywhere). A bar wearing a NEW
// pill with nothing behind it would be the page announcing something that has
// not been announced — and a draft's TITLE is the announcement, so a
// "something is coming" bar leaks the same fact more quietly.
//
// So when there is nothing, this is absent. /releases already carries the one
// flat sentence for that state, on the page whose subject it is.

/** How many posts the expanded bar lists before sending people to the page. */
const SHOWN = 3

const DAY = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  // UTC for src/routes/Releases.jsx's reason: a release date is a fact about the
  // repo, not about the reader, and the browser's zone gives two answers either
  // side of midnight.
  timeZone: 'UTC',
})

function dayLabel(iso) {
  const parsed = new Date(`${iso}T00:00:00Z`)
  return Number.isNaN(parsed.getTime()) ? null : DAY.format(parsed)
}

export default function ReleaseBar() {
  const [open, setOpen] = useState(false)
  const [openPost, setOpenPost] = useState(0)

  const entries = Array.isArray(releases.entries) ? releases.entries : []
  if (entries.length === 0) return null

  const newest = entries[0]
  const listed = entries.slice(0, SHOWN)
  const newestDay = dayLabel(newest.date)

  return (
    <section className="d2d-portal__releases" aria-labelledby="portal-releases">
      <span className="d2d-portal__spine" aria-hidden="true" />
      <h2 id="portal-releases" className="d2d-sr-only">
        Release notes
      </h2>
      <Disclosure
        open={open}
        onToggle={() => setOpen((was) => !was)}
        className="d2d-portal__releasebar"
        data-testid="release-bar"
      >
        {/* The word carries it, the fill is decoration — the page's colour rule
            applied to the one ember region above the fold. */}
        <span className="d2d-portal__new d2d-micro">New</span>
        <span className="d2d-portal__releasehead">
          <span className="d2d-portal__releasetitle">{newest.title}</span>
          <span className="d2d-portal__releasemeta d2d-micro">
            {open
              ? `${listed.length} post${listed.length === 1 ? '' : 's'} · newest first`
              : `${newestDay ?? 'Undated'} · tap to read`}
          </span>
        </span>
      </Disclosure>

      {open ? (
        <ul className="d2d-portal__releaselist">
          {listed.map((entry, index) => (
            <li key={entry.id}>
              <Disclosure
                open={openPost === index}
                onToggle={() => setOpenPost((was) => (was === index ? -1 : index))}
                className="d2d-portal__releasepost"
              >
                <span className="d2d-portal__releasehead">
                  <span className="d2d-portal__releaseposttitle">{entry.title}</span>
                  <span className="d2d-portal__releasemeta d2d-micro">
                    {[dayLabel(entry.date), entry.version ? `version ${entry.version}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </Disclosure>
              {openPost === index ? (
                <div className="d2d-portal__releasebody">
                  <p>{entry.summary}</p>
                  {/* A real route, not a "#" — the design's frame uses a
                      placeholder href and a link that goes nowhere is worse
                      than no link. /releases renders every post on one page,
                      anchored by the release's own heading id. */}
                  <Link className="d2d-portal__more d2d-micro" to={`/releases#${entry.anchor}`}>
                    Read the post →
                  </Link>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
